import { match } from 'ts-pattern'
import i18next from 'i18next'
import type { VolumedAssignPlanMutator } from '@/lib/entity/volumed_assign_plan'
import type { Converter } from '@/lib/converter/entityFormatter'
import { layered } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'
import { sql } from 'kysely'
import { asModifiedField } from '@/lib/converter/entityFormatter'
import { formatForDisplay } from '@/lib/utils/unitTime'

type VolumedAssignPlanSchema = VolumedAssignPlanMutator['merged']

export const VolumedAssignPlanConverter: Converter<VolumedAssignPlanSchema> = {
  icon: () => i18next.t('stages.icon.volumed_assign_plan'),
  title: () => i18next.t('stages.table.volumed_assign_plan'),
  subTitle: async (merged) => {
    const queryResult = await assignQuery(merged.volumed_assign_id!)
    const subTitle = queryResult.nameConcat
    return subTitle.merged() ?? subTitle.BASE ?? ''
  },
  modifiedFields: (modified, column, context) => {
    const label = i18next.t(`stages.labels.assign_plan.${column}`)
    return match(column)
      .with('start_offset', async () => {
        const queryResult = await offsetCalculateQuery(
          context!.volumed_assign_id!,
          modified.start_offset!
        )
        return asModifiedField(label, queryResult.offset.merged())
      })
      .with('end_offset', async () => {
        const queryResult = await offsetCalculateQuery(
          context!.volumed_assign_id!,
          modified.end_offset!
        )
        return asModifiedField(label, queryResult.offset.merged())
      })
      .with('estimate', () =>
        asModifiedField(
          label,
          `${formatForDisplay['PLAN_HOURS'](modified.estimate ?? 0)} ${i18next.t('ui.assignColumn.modal.hoursPerDay')}`
        )
      )
      .with('id', 'volumed_assign_id', () => undefined)
      .exhaustive()
  }
}
const assignQuery = async (id: string) => {
  return await layered(async (db: Kysely<DB>) => {
    const result = await db
      .selectFrom('volumed_assign')
      .innerJoin('project', 'volumed_assign.project_id', 'project.id')
      .innerJoin('job_title', 'volumed_assign.job_title_id', 'job_title.id')
      .select([sql<string>`project.name || ' x ' || job_title.name`.as('nameConcat')])
      .where('volumed_assign.id', '=', id)
      .executeTakeFirst()
    if (!result) {
      return { nameConcat: null }
    }
    return result
  })
}

const offsetCalculateQuery = async (assignId: string, value: number) => {
  return await layered(async (db: Kysely<DB>) => {
    const result = await db
      .selectFrom('project')
      .innerJoin('volumed_assign', 'project.id', 'volumed_assign.project_id')
      .select([sql<string>`date(project.start_date, '+' || ${value} || ' days')`.as('offset')])
      .where('volumed_assign.id', '=', assignId)
      .executeTakeFirst()
    if (!result) {
      return { offset: null }
    }
    return result
  })
}
